<template>
  <div class="player-list-view">
    <ftu-block
      dismiss-key="players"
      icon="sports_handball"
      title="These are players"
      description="The players below are indexes that track the aggregate daily value movements of all verified cards of that player. Use these indexes to research specific player markets within the hobby to gauge where the markets are moving."
    />

    <page-header title="Players" />

    <align flex collapse-end direction="flex-start">
      <shared-filters
        show-recent-searches
        :filters.sync="filters"
        :query.sync="query"
        :properties="defaultFilters"
        @clear="clearFilters"
      />

      <direction-sort-options
        :sort.sync="sort"
        :direction.sync="direction"
        :options="sortOptions"
      />
    </align>

    <filter-results
      :is-filtering="isFiltering"
      :filters="filters"
      :results="numResults && numResults < 10000 ? numResults : null"
      @clear="clearFilters"
    />

    <div class="results">
      <empty-state
        v-if="showEmptyState"
        icon="search_off"
        title="There are no results for your search"
        description="Try adjusting your search"
      />

      <template v-else-if="loaded">
        <div class="list">
          <index-list-item
            v-for="player in players"
            :key="player.player"
            :to="`/players/${player.player}`"
            :index="player"
            :fields="statFields"
            as="router-link"
          />
        </div>

        <infinite-scroll
          :loading="loading"
          :has-more-data="hasMoreData"
          @load="nextPage"
        />
      </template>

      <spinner v-else />
    </div>

    <scroll-top />
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import clone from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import * as Sentry from '@sentry/vue';

import {
  CATEGORY_OPTIONS,
  PLAYERS_SORT_OPTIONS,
  DEFAULT_PLAYER_INDEX_FILTERS,
} from '@cardladder/shared/constants';

import DirectionSortOptions from '@cardladder/shared/components/DirectionSortOptions';
import IndexListItem from '@cardladder/shared/components/IndexListItem';
import SharedFilters from '@/components/SharedFilters';
import FilterResults from '@/components/FilterResults';

import { searchService } from '@/services';

export default {
  components: {
    DirectionSortOptions,
    FilterResults,
    IndexListItem,
    SharedFilters,
  },

  data() {
    return {
      limit: 20,
      index: {},
      players: [],
      numResults: 0,
      page: 1,
      hasMoreData: true,
      loading: false,
      loaded: false,
      defaultFilters: DEFAULT_PLAYER_INDEX_FILTERS,
      sortOptions: PLAYERS_SORT_OPTIONS,
      categoryOptions: CATEGORY_OPTIONS,
    };
  },

  created() {
    this.reset = debounce(this.reset, 500);

    if (this.$route.query.q) {
      this.query = this.$route.query.q;
    }

    this.loadPlayers();
  },

  watch: {
    page(value) {
      if (value > 1) {
        this.loadPlayers();
      }
    },

    filters: {
      handler() {
        this.reset();
      },
      deep: true,
    },

    sort() {
      this.reset();
    },

    direction() {
      this.reset();
    },

    query() {
      this.reset();
    },

    '$route.query.q': {
      handler(value) {
        this.query = value;
      },
    },
  },

  computed: {
    sort: {
      get() {
        const activeIndexSort = this.sortOptions.find((value) => value === this.$store.state.indexSort);

        if (activeIndexSort) {
          return this.$store.state.indexSort;
        }

        return this.sortOptions[0];
      },

      set(value) {
        this.$store.commit('SET_INDEX_SORT', value);
      },
    },

    filters: {
      get() {
        return this.$store.state.playerIndexFilterMap;
      },

      set(value) {
        this.$store.commit('SET_PLAYER_INDEX_FILTERS', value);
      },
    },

    direction: {
      get() {
        return this.$store.state.indexDirection;
      },

      set(value) {
        this.$store.commit('SET_INDEX_DIRECTION', value);
      },
    },

    query: {
      get() {
        return this.$store.state.playerIndexQuery;
      },

      set(value) {
        this.$store.commit('SET_PLAYER_INDEX_QUERY', value);
      },
    },

    showEmptyState() {
      return !this.loading && this.loaded && this.players.length === 0;
    },

    isFiltering() {
      return Boolean(this.query?.trim().length > 0) || !isEqual(this.filters, this.defaultFilters);
    },

    statFields() {
      const fields = [...PLAYERS_SORT_OPTIONS];

      return fields.sort((a, b) => {
        if (a === this.sort) return -1;
        if (b === this.sort) return 1;

        return 0;
      })
      .slice(0, 3)
      .reverse();
    },
  },

  methods: {
    async reset() {
      this.loaded = false;
      this.page = 1;
      this.hasMoreData = true;

      await this.loadPlayers(true);
    },

    async loadPlayers(reset = false) {
      try {
        this.loading = true;

        const data = await searchService.search({
          index: 'players',
          query: this.query,
          filters: this.filters,
          page: this.page,
          limit: this.limit,
          sort: this.sort,
          direction: this.direction,
        });

        const players = data.hits;

        this.numResults = data.totalHits;

        if (reset) {
          this.players = players;
        } else {
          this.players = [...this.players, ...players];
        }

        if (players.length < this.limit) {
          this.hasMoreData = false;
        }
      } catch (e) {
        Sentry.captureException(e);
      }

      this.loading = false;
      this.loaded = true;
    },

    nextPage() {
      this.page += 1;
    },

    clearFilters() {
      this.filters = clone(this.defaultFilters);
    },
  },
};
</script>

<style lang="scss" scoped>
.player-list-view {
  @include container;
  min-height: 100%;

  h4 {
    margin: 0;
  }
}

.results {
  position: relative;
  flex: 1;
}

.input {
  max-width: 450px;
  --theme-icon-button-color: #{$color-gray-5};
  --text-input-margin-bottom: 0;

  @media($medium-down) {
    max-width: none;
  }
}

.search-container {
  @media($medium-down) {
    width: 100%;
  }
}

.list {
  margin-top: $gp;

  @media($medium-down) {
    margin: $gp (-$gp) 0;
  }
}
</style>
